import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Rocket, Loader2, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "../components/PasswordInput";

import { _t } from "@/lib/i18n";
export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "", surname: "", username: "", email: "", password: "",
    consent_terms: false, consent_privacy: false, consent_marketing: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent_terms || !form.consent_privacy) {
      setError(_t("Per creare l'account devi accettare i Termini di servizio e l'Informativa sulla privacy."));
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(_t("Benvenuto"));
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 cover-sunset">
        <div className="h-full flex flex-col justify-between p-16">
          <Link to="/" className="flex items-center gap-3">
            <Rocket className="w-6 h-6 text-[#FF5722] -rotate-45" />
            <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF5722] mb-4">{_t("Inizia")}</div>
            <h1 className="font-display text-6xl text-white leading-tight mb-6">{_t("Pronto al decollo?")}</h1>
            <p className="text-white/70 max-w-md leading-relaxed">{_t("Crea il tuo primo caveau in meno di un minuto. Nessuna carta di credito.")}</p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("MMXXVI · Custodi del tempo")}</div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF5722] mb-3">{_t("Registrati")}</div>
            <h2 className="font-display text-4xl text-white mb-2">{_t("Crea il tuo account")}</h2>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <div><label className="tc-label">{_t("Nome")}</label><input data-testid="register-name" value={form.name} onChange={upd("name")} className="tc-input" /></div>
            <div><label className="tc-label">{_t("Cognome")}</label><input data-testid="register-surname" value={form.surname} onChange={upd("surname")} className="tc-input" /></div>
          </div>
          <div className="space-y-5 mb-6">
            <div><label className="tc-label">{_t("Username")}</label><input data-testid="register-username" value={form.username} onChange={upd("username")} required minLength={3} className="tc-input" placeholder={_t("es. alice")} /></div>
            <div><label className="tc-label">{_t("Email")}</label><input data-testid="register-email" type="email" value={form.email} onChange={upd("email")} required className="tc-input" placeholder="tu@esempio.com" /></div>
            <div><label className="tc-label">{_t("Password")}</label><PasswordInput testId="register-password" value={form.password} onChange={upd("password")} required minLength={6} autoComplete="new-password" placeholder={_t("minimo 6 caratteri")} /></div>
          </div>

          {/* Consensi obbligatori */}
          <div className="mb-4 space-y-3">
            <ConsentRow
              testId="consent-terms"
              checked={form.consent_terms}
              onChange={updBool("consent_terms")}
              required
              icon={<ShieldCheck className="w-4 h-4 text-[#FF5722]" />}
              title={_t("Termini di servizio e condizioni d'uso")}
              description={<>{_t("Confermo di aver letto e di accettare i")}{" "}<Link to="/faq" target="_blank" className="text-[#FF5722] underline underline-offset-2">{_t("Termini di servizio")}</Link>{" "}{_t("di Time Capsule.")}</>}
            />
            <ConsentRow
              testId="consent-privacy"
              checked={form.consent_privacy}
              onChange={updBool("consent_privacy")}
              required
              icon={<ShieldCheck className="w-4 h-4 text-[#FF5722]" />}
              title={_t("Informativa sulla privacy e trattamento dei dati personali")}
              description={<>{_t("Ho letto l'")}<Link to="/sicurezza" target="_blank" className="text-[#FF5722] underline underline-offset-2">{_t("Informativa sulla privacy")}</Link>{" "}{_t("e acconsento al trattamento dei miei dati personali per l'erogazione del servizio (art. 6 GDPR).")}</>}
            />
          </div>

          {/* Consenso facoltativo — chiaramente separato */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{_t("Facoltativo · puoi revocarlo in qualsiasi momento")}</div>
            <ConsentRow
              testId="consent-marketing"
              checked={form.consent_marketing}
              onChange={updBool("consent_marketing")}
              optional
              icon={<Mail className="w-4 h-4 text-white/60" />}
              title={_t("Comunicazioni promozionali e pubblicità personalizzata")}
              description={_t("Acconsento a ricevere comunicazioni promozionali e pubblicità personalizzata da parte di Time Capsule e dei suoi inserzionisti partner. Questo consenso non è necessario per usare l'app.")}
            />
          </div>

          {error && <div data-testid="register-error" className="mb-6 text-sm text-red-300 border border-red-500/30 bg-red-500/10 rounded-md p-3">{error}</div>}

          <button data-testid="register-submit" type="submit" disabled={loading} className="btn-launch w-full">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Creazione...")}</> : <><Rocket className="w-4 h-4 -rotate-45" />{" "}{_t("Registrati")}</>}
          </button>

          <p className="text-center mt-8 text-sm text-white/60">{_t("Hai già un account?")}{" "}<Link to="/login" data-testid="link-login" className="text-[#FF5722] hover:underline underline-offset-4">{_t("Accedi")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function ConsentRow({ testId, checked, onChange, required, optional, icon, title, description }) {
  return (
    <label className={`flex gap-3 items-start p-4 rounded-lg border cursor-pointer transition-colors ${checked ? "border-[#FF5722]/50 bg-[#FF5722]/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}>
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={onChange}
        className="mt-1 w-4 h-4 accent-[#FF5722] shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <div className="text-white text-sm font-medium">{title}</div>
          {required && <span className="text-[9px] uppercase tracking-[0.2em] text-[#FF5722] border border-[#FF5722]/40 rounded px-1.5 py-0.5">{_t("Obbligatorio")}</span>}
          {optional && <span className="text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/20 rounded px-1.5 py-0.5">{_t("Facoltativo")}</span>}
        </div>
        <div className="text-white/60 text-xs leading-relaxed">{description}</div>
      </div>
    </label>
  );
}
